/**
 * Side Cart Component
 * 
 * Main side cart drawer component that displays cart items and totals.
 * Replicates TheSpadr sidecart behavior.
 */

'use client';

import styled from '@emotion/styled';
import {
  Drawer,
} from '@template-project/afrodita-components';
import { useTranslation } from '@/lib/i18n/client';
import { useIntl } from '@/lib/intl';
import {
  useCart,
  useUpdateCartItem,
  useRemoveCartItem,
  useUpdatePackQuantity,
  useRemovePackFromCart,
} from '@/lib/hooks/use-cart';
import { CartHeader } from './CartHeader/CartHeader';
import { CartItems } from './CartItems/CartItems';
import { EmptyCart } from './EmptyCart/EmptyCart';
import { CartSummaryV2 } from './CartSummaryV2';
import type { CartSummaryVariant } from './CartSummaryV2';
import { storefrontLayoutLogger } from '@/lib/logger';

const DrawerWrapper = styled.div`
  > div > div {
    z-index: 1000;
  }
`;

const Main = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Lines = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  min-height: 0;
  padding: 0 ${({ theme }) => theme.space.md};
`;

const Footer = styled.div`
  padding: ${({ theme }) => theme.space.md};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.border.color};
  box-shadow: 0px 13px 4px 15px ${({ theme }) => theme.colors.border.color};
  z-index: 10;
  background: ${({ theme }) => theme.colors.background.primary};
`;

const DEFAULT_DESKTOP_WIDTH = '470px';
const MOBILE_WIDTH = '95vw';

export interface SideCartSlots {
  /** Slim banner above the header — funnel promotions, urgency messages */
  topBanner?: React.ReactNode;
  /** Custom header content (rendered below the default CartHeader) */
  header?: React.ReactNode;
  /** Content between the total and the checkout button — upsell offers, bundles */
  offerFooter?: React.ReactNode;
  /** Content below the checkout button — trust badges, links, policies */
  menuFooter?: React.ReactNode;
}

export interface SideCartProps {
  cartToken: string | null;
  isOpen: boolean;
  onClose: () => void;
  onCheckout?: (cartToken: string) => void;
  onCartUpdate?: (cartToken: string) => void;
  onCartCompleted?: () => void;
  isMobile?: boolean;
  /** Which CartSummaryV2 layout to use. Defaults to 'free-shipping-bar'. */
  summaryVariant?: CartSummaryVariant;
  /** Configurable content slots for funnel experiences and extensions */
  slots?: SideCartSlots;
}

export function SideCart({
  cartToken,
  isOpen,
  onClose,
  onCheckout,
  onCartUpdate,
  onCartCompleted,
  isMobile = false,
  summaryVariant = 'free-shipping-bar',
  slots,
}: SideCartProps) {
  const { t } = useTranslation('sidecart');
  const { countryConfig } = useIntl();
  const { data: cart, isLoading, error: fetchError } = useCart(cartToken, onCartCompleted);
  const updateCartItemMutation = useUpdateCartItem();
  const removeCartItemMutation = useRemoveCartItem();
  const updatePackQuantityMutation = useUpdatePackQuantity();
  const removePackMutation = useRemovePackFromCart();
  const cartMutationLoading =
    updateCartItemMutation.isPending ||
    removeCartItemMutation.isPending ||
    updatePackQuantityMutation.isPending ||
    removePackMutation.isPending;
  const priceOptions = {
    currency: countryConfig.currency.code,
    locale: countryConfig.locale,
    symbolOverride: countryConfig.currency.format,
  };

  const handleQuantityChange = async (productId: string, variantId: string, quantity: number) => {
    if (!cartToken) {
      return;
    }

    try {
      const result = await updateCartItemMutation.mutateAsync({
        cartToken,
        productId,
        variantId,
        quantity,
      });

      if (onCartUpdate) {
        onCartUpdate(result.cart.token);
      }
    } catch (error) {
      storefrontLayoutLogger.error('Failed to update cart item', error instanceof Error ? error : undefined, {
        productId,
        variantId,
        quantity,
      });
    }
  };

  const handlePackQuantityChange = async (packId: string, quantity: number) => {
    if (!cartToken) {
      return;
    }

    try {
      const result = await updatePackQuantityMutation.mutateAsync({
        cartToken,
        packId,
        quantity,
      });

      if (onCartUpdate) {
        onCartUpdate(result.cart.token);
      }
    } catch (error) {
      storefrontLayoutLogger.error(
        'Failed to update pack quantity',
        error instanceof Error ? error : undefined,
        { packId, quantity },
      );
    }
  };

  const handlePackRemove = async (packId: string) => {
    if (!cartToken) {
      return;
    }

    try {
      const result = await removePackMutation.mutateAsync({ cartToken, packId });

      if (onCartUpdate) {
        onCartUpdate(result.cart.token);
      }
    } catch (error) {
      storefrontLayoutLogger.error(
        'Failed to remove pack from cart',
        error instanceof Error ? error : undefined,
        { packId },
      );
    }
  };

  const handleRemove = async (productId: string, variantId: string) => {
    if (!cartToken) {
      return;
    }

    try {
      const result = await removeCartItemMutation.mutateAsync({
        cartToken,
        productId,
        variantId,
      });

      if (onCartUpdate) {
        onCartUpdate(result.cart.token);
      }
    } catch (error) {
      storefrontLayoutLogger.error('Failed to remove cart item', error instanceof Error ? error : undefined, {
        productId,
        variantId,
      });
    }
  };

  const handleCheckout = () => {
    if (cartToken && onCheckout) {
      onCheckout(cartToken);
    }
  };

  const cartItemsQuantity = cart?.items.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const total = cart ? cart.totals.total.display : 0;
  const savings = cart ? cart.totals.discounts.display : 0;
  const compareAtTotal = cart
    ? cart.items.reduce((sum, item) => {
        const compareAt = item.compare_at_price?.display ?? item.price.display;
        return sum + compareAt;
      }, 0)
    : undefined;
  const isProceedDisabled = !cart || total <= 0 || isLoading;

  return (
    <DrawerWrapper>
      <Drawer
        isOpen={isOpen}
        onClose={isOpen ? onClose : undefined}
        width={isMobile ? MOBILE_WIDTH : DEFAULT_DESKTOP_WIDTH}
      >
        <Main>
          {isLoading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              {t('cart.loading')}
            </div>
          ) : !cart || cart.items.length === 0 ? (
            <EmptyCart
              onClose={onClose}
              onContinueShopping={onClose}
            />
          ) : (
            <>
              {/* Slot: topBanner — slim promotional banner above everything */}
              {slots?.topBanner}

              <CartHeader
                itemCount={cartItemsQuantity}
                onClose={onClose}
              />

              {/* Slot: header — custom content below the cart header */}
              {slots?.header}

              <Lines>
                <CartItems
                  items={cart.items}
                  isLoading={cartMutationLoading}
                  onQuantityChange={handleQuantityChange}
                  onRemove={handleRemove}
                  onPackQuantityChange={handlePackQuantityChange}
                  onPackRemove={handlePackRemove}
                />
              </Lines>

              <Footer>
                <CartSummaryV2
                  variant={summaryVariant}
                  total={total}
                  compareAtTotal={compareAtTotal}
                  savings={savings}
                  onCheckout={handleCheckout}
                  isCheckoutDisabled={isProceedDisabled}
                  error={fetchError ? String(fetchError) : null}
                  priceOptions={priceOptions}
                  offerFooter={slots?.offerFooter}
                  menuFooter={slots?.menuFooter}
                />
              </Footer>
            </>
          )}
        </Main>
      </Drawer>
    </DrawerWrapper>
  );
}
